import "./App.css";
import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, Outlet, useLocation, useNavigate, Navigate } from "react-router-dom";

import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Home from "./components/Home";
import RequireAuth from "./components/RequireAuth";
import { bootstrapAuth } from "./utils/auth";
import { ensureCsrfToken } from "./api/client";
import Seo from "./components/Seo";

const Dashboard = lazy(() => import("./components/Dashboard"));
const CategoriesPage = lazy(() => import("./components/Categories/CategoriesPage/CategoriesPage"));
const MaterialList = lazy(() => import("./components/Categories/MaterialList/MaterialList"));
const MaterialDetail = lazy(() => import("./components/Categories/MaterialDetail/MaterialDetail"));
const PetList = lazy(() => import("./components/Categories/PetList/PetList"));
const PetDetail = lazy(() => import("./components/Categories/PetDetail/PetDetail"));
const HangoutList = lazy(() => import("./components/Categories/HangoutList/HangoutList"));
const RideList = lazy(() => import("./components/Categories/RideList/RideList"));
const HangoutDetail = lazy(() => import("./components/Categories/HangoutDetail/HangoutDetail"));
const RideDetail = lazy(() => import("./components/Categories/RideDetail/RideDetail"));
const AdviceOptions = lazy(() => import("./components/Categories/AdviceOptions"));
const AdviceThreadPage = lazy(() => import("./components/Categories/AdviceHub/AdviceThreadPage"));
const Profiles = lazy(() => import("./components/Profiles"));
const ProfileDetail = lazy(() => import("./components/ProfileDetail"));
const MyProfile = lazy(() => import("./components/MyProfile"));
const EditProfile = lazy(() => import("./components/EditProfile"));
const ContactPage = lazy(() => import("./components/ContactPage"));
const PrivacyPage = lazy(() => import("./components/PrivacyPage"));
const TermsPage = lazy(() => import("./components/TermsPage"));
const MessagesPage = lazy(() => import("./components/MessagesPage"));
const Login = lazy(() => import("./components/Login"));
const AdminDashboard = lazy(() => import("./components/AdminDashboard"));
const UploadMaterialPage = lazy(() => import("./components/Upload/UploadMaterialPage"));
const UploadPetPage = lazy(() => import("./components/Upload/UploadPetPage"));
const UploadHangoutPage = lazy(() => import("./components/Upload/UploadHangoutPage"));
const UploadRidePage = lazy(() => import("./components/Upload/UploadRidePage"));
const SearchResults = lazy(() => import("./components/SearchResults"));
const SocialAuthCallback = lazy(() => import("./components/SocialAuthCallback"));
const MyListingsDashboard = lazy(() => import("./components/MyListingsDashboard"));
const ForgotPassword = lazy(() => import("./components/ForgotPassword"));
const ResetPassword = lazy(() => import("./components/ResetPassword"));
const VerifyEmailPage = lazy(() => import("./components/VerifyEmailPage"));
const LikedItemsPage = lazy(() => import("./components/LikedItemsPage"));


function AuthBootstrap() {
  useEffect(() => {
    ensureCsrfToken().catch(() => {});
    bootstrapAuth();
  }, []);

  return null;
}

function Layout() {
  const location = useLocation();
  const navigate = useNavigate();

  const goToPage = (target) => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const el = document.getElementById(target);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
      return;
    }
    const el = document.getElementById(target);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="App app-shell">
      <Seo />
      <a href="#app-content" className="skip-link">Skip to main content</a>
      <Navbar goToPage={goToPage} />
      <main id="app-content" className="app-main" tabIndex="-1">
        <Suspense fallback={<div className="app-loading">Loading…</div>}>
          <Outlet />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}

function HomeRoute() {
  const navigate = useNavigate();
  return <Home onNavigate={navigate} />;
}

function MessagesRoute() {
  const navigate = useNavigate();
  return <MessagesPage onClose={() => navigate(-1)} />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthBootstrap />
      <Routes>
        <Route element={<Layout />}>
          <Route path="/" element={<HomeRoute />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/categories" element={<CategoriesPage />} />
          <Route path="/materials" element={<MaterialList />} />
          <Route path="/materials/:id" element={<MaterialDetail />} />
          <Route path="/pets" element={<PetList />} />
          <Route path="/pets/:id" element={<PetDetail />} />
          <Route path="/hangouts" element={<HangoutList />} />
          <Route path="/hangouts/:id" element={<HangoutDetail />} />
          <Route path="/rides" element={<RideList />} />
          <Route path="/rides/:id" element={<RideDetail />} />
          <Route path="/advice" element={<AdviceOptions />} />
          <Route path="/advice/:threadId" element={<AdviceThreadPage />} />
          <Route path="/adviceanswers" element={<Navigate to="/advice" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/auth/callback" element={<SocialAuthCallback />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/profiles" element={<Profiles />} />
          <Route path="/profiles/:id" element={<ProfileDetail />} />
          <Route path="/profile" element={<RequireAuth><MyProfile /></RequireAuth>} />
          <Route path="/profile/edit" element={<RequireAuth><EditProfile /></RequireAuth>} />
          <Route path="/my-listings" element={<RequireAuth><MyListingsDashboard /></RequireAuth>} />
          <Route path="/liked-items" element={<RequireAuth><LikedItemsPage /></RequireAuth>} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/messages" element={<RequireAuth><MessagesRoute /></RequireAuth>} />
          <Route path="/search" element={<SearchResults />} />
          <Route path="/upload/material" element={<RequireAuth><UploadMaterialPage /></RequireAuth>} />
          <Route path="/upload/pet" element={<RequireAuth><UploadPetPage /></RequireAuth>} />
          <Route path="/upload/hangout" element={<RequireAuth><UploadHangoutPage /></RequireAuth>} />
          <Route path="/upload/ride" element={<RequireAuth><UploadRidePage /></RequireAuth>} />
          <Route path="/admin" element={<RequireAuth><AdminDashboard /></RequireAuth>} />
          <Route path="*" element={<HomeRoute />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
